import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Plus, Repeat, RotateCcw, Trash2, X } from 'lucide-react';
import { Button, Drawer } from '@/design/components';
import { cn } from '@/lib/cn';
import { formatDate, toDateKey, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { formatHours, shiftMinutes } from '../hours';
import { useRecurringFor, useRecurringMutations } from './recurringApi';
import { useShiftTaskMutations, useShiftTasks, useTaskTemplates } from './shiftTasksApi';
import type { WeekAssignment, WeekBoard } from './weekApi';

const hhmm = (time: string) => time.slice(0, 5);

/**
 * עריכת משמרת אחת, במגש לצד הלוח.
 *
 * לא דיאלוג: בזמן שמשנים שעות של אדם אחד רוצים להמשיך לראות את
 * שאר השבוע, כי השאלה "עד מתי הוא נשאר" תלויה במי מגיע אחריו.
 *
 * שעות ריקות הן המצב הרגיל, ופירושן "כמו המשמרת". לכן יש כפתור
 * חזרה ולא רק מחיקת הטקסט: שינוי בשעות המשמרת עצמה ימשיך לחול
 * על מי שלא נקבע לו אחרת.
 */
export function ShiftDrawer({
  board,
  assignment,
  isPublished,
  isPending,
  onSave,
  onRemove,
  onClose,
}: {
  board: WeekBoard;
  assignment: WeekAssignment;
  isPublished: boolean;
  isPending: boolean;
  onSave: (input: {
    start_time: string | null;
    end_time: string | null;
    note: string | null;
    shift_type_id: string;
  }) => Promise<unknown>;
  onRemove: () => void;
  onClose: () => void;
}) {
  const person = board.staff.find((one) => one.id === assignment.employee_id);
  const branch = board.branches.find((one) => one.id === assignment.branch_id);

  const [shiftTypeId, setShiftTypeId] = useState(assignment.shift_type_id);
  const [start, setStart] = useState(assignment.start_time ? hhmm(assignment.start_time) : '');
  const [end, setEnd] = useState(assignment.end_time ? hhmm(assignment.end_time) : '');
  const [note, setNote] = useState(assignment.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const shift = board.shift_types.find((one) => one.id === shiftTypeId);
  const isCustom = start !== '' || end !== '';
  const effectiveStart = start || (shift ? hhmm(shift.start_time) : '');
  const effectiveEnd = end || (shift ? hhmm(shift.end_time) : '');

  // אילוץ של אותו אדם באותו יום, כדי שלא יתגלה רק אחרי שמירה
  const conflict = board.constraints.find(
    (one) =>
      one.employee_id === assignment.employee_id &&
      one.date === assignment.date &&
      (one.shift_type_id === null || one.shift_type_id === shiftTypeId),
  );

  /** `close` היא הסגירה המונפשת של המגש, ולא ההסרה מה DOM. */
  function save(close: () => void) {
    if ((start === '') !== (end === '')) {
      setError('צריך למלא שעת התחלה ושעת סיום, או להשאיר את שתיהן ריקות.');
      return;
    }
    setError(null);
    void onSave({
      start_time: start === '' ? null : start,
      end_time: end === '' ? null : end,
      note: note.trim() === '' ? null : note.trim(),
      shift_type_id: shiftTypeId,
    })
      .then(close)
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  return (
    <Drawer
      title={person?.name ?? 'משמרת'}
      subtitle={`${weekdayName(assignment.date)}, ${formatDate(assignment.date)} · ${branch?.name ?? ''}`}
      onClose={onClose}
      footer={(close) =>
        isPublished ? (
          <p className="m-0 text-body-sm text-muted">
            הסידור פורסם. כדי לשנות אותו צריך לפתוח אותו מחדש במסך הסניף.
          </p>
        ) : (
          <>
            <Button variant="primary" disabled={isPending} onClick={() => save(close)}>
              שמירת השינויים
            </Button>
            <Button variant="secondary" onClick={close}>
              ביטול
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                close();
                onRemove();
              }}
            >
              <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
              הסרה מהמשמרת
            </Button>
          </>
        )
      }
    >
      <div className="grid gap-4">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {conflict && (
          <p
            className={`m-0 rounded-sm border p-2 text-body-sm ${
              conflict.is_blocking
                ? 'border-bad/40 bg-bad/10 text-bad'
                : 'border-line bg-cream text-ink'
            }`}
          >
            {person?.name} סימן/ה &quot;{conflict.name}&quot; ביום הזה
            {conflict.note ? `: ${conflict.note}` : '.'}
          </p>
        )}

        <label className="grid gap-1">
          <span className="text-label text-muted">משמרת</span>
          <select
            value={shiftTypeId}
            disabled={isPublished}
            onChange={(event) => setShiftTypeId(event.target.value)}
            className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink"
          >
            {board.shift_types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({hhmm(type.start_time)}–{hhmm(type.end_time)})
              </option>
            ))}
          </select>
        </label>

        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="p-0 text-label text-muted">שעות</legend>

          <div className="grid grid-cols-2 gap-2">
            <label className="grid gap-1">
              <span className="text-label text-muted">התחלה</span>
              <input
                type="time"
                value={start}
                disabled={isPublished}
                placeholder={shift ? hhmm(shift.start_time) : ''}
                onChange={(event) => setStart(event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-label text-muted">סיום</span>
              <input
                type="time"
                value={end}
                disabled={isPublished}
                placeholder={shift ? hhmm(shift.end_time) : ''}
                onChange={(event) => setEnd(event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink"
              />
            </label>
          </div>

          <p className="m-0 flex flex-wrap items-center gap-2 text-body-sm text-muted">
            <span>
              {isCustom ? 'שעות מיוחדות' : 'שעות המשמרת'}:{' '}
              <span className="tabular text-ink" dir="ltr">
                {effectiveStart}–{effectiveEnd}
              </span>
            </span>
            {effectiveStart && effectiveEnd && (
              <span>· {formatHours(shiftMinutes(effectiveStart, effectiveEnd))}</span>
            )}
          </p>

          {isCustom && !isPublished && (
            <Button
              variant="ghost"
              onClick={() => {
                setStart('');
                setEnd('');
              }}
            >
              <RotateCcw size={18} strokeWidth={1.75} aria-hidden="true" />
              חזרה לשעות המשמרת
            </Button>
          )}
        </fieldset>

        <label className="grid gap-1">
          <span className="text-label text-muted">הערה למשמרת</span>
          <textarea
            value={note}
            rows={3}
            disabled={isPublished}
            placeholder="למשל: מגיע/ה אחרי פתיחת החנות"
            onChange={(event) => setNote(event.target.value)}
            className="rounded-sm border border-muted bg-raised p-2 text-body text-ink placeholder:text-muted"
          />
        </label>

        {assignment.source === 'copied' && (
          <p className="m-0 text-label text-muted">
            המשמרת הזו הועתקה מהשבוע הקודם ועוד לא נבדקה.
          </p>
        )}
        {assignment.source === 'recurring' && (
          <p className="m-0 text-label text-muted">
            המשמרת הזו נוצרה ממשמרת קבועה. מחיקה כאן אינה מבטלת את הקבועה.
          </p>
        )}

        <RecurringToggle
          assignment={assignment}
          shiftName={shift?.name ?? ''}
          isPublished={isPublished}
        />

        <ShiftTasks
          assignment={assignment}
          jobTitleId={person?.job_title_id ?? null}
          isPublished={isPublished}
        />
      </div>
    </Drawer>
  );
}

/**
 * הפיכת המשמרת הזו לקבועה, מהמקום שבו רואים אותה.
 *
 * המנהל שמסתכל על "נועה, שני, בוקר, טאגור" הוא היחיד שיודע אם
 * זה חד פעמי או קבוע, וזה הרגע שבו הוא יודע את זה. מסך נפרד
 * לניהול תבניות היה דורש להקליד שוב את מה שכבר על המסך.
 *
 * התבנית חלה מהשבוע הבא ואילך: השבוע הזה כבר משובץ, ומילוי חוזר
 * שלו לא יוסיף כלום.
 */
function RecurringToggle({
  assignment,
  shiftName,
  isPublished,
}: {
  assignment: WeekAssignment;
  shiftName: string;
  isPublished: boolean;
}) {
  const recurring = useRecurringFor(assignment.employee_id);
  const { create, remove } = useRecurringMutations();
  const [error, setError] = useState<string | null>(null);

  const weekday = new Date(`${assignment.date}T12:00:00`).getDay();
  const existing = recurring.data?.find((one) => one.weekday === weekday);
  const matchesThis =
    existing !== undefined &&
    existing.branch_id === assignment.branch_id &&
    existing.shift_type_id === assignment.shift_type_id;

  function run(promise: Promise<unknown>) {
    setError(null);
    void promise.catch((caught: unknown) => setError(errorMessage(caught)));
  }

  return (
    <section className="grid gap-2 border-t border-line pt-4">
      <h3 className="m-0 flex items-center gap-2 text-body font-medium text-ink">
        <Repeat size={16} strokeWidth={1.75} aria-hidden="true" />
        משמרת קבועה
      </h3>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {matchesThis ? (
        <>
          <p className="m-0 text-body-sm text-ink">
            חוזרת כל יום {weekdayName(assignment.date)}, {shiftName}.
          </p>
          <p className="m-0 text-label text-muted">
            כל שבוע חדש שייפתח יגיע עם המשמרת הזו כבר משובצת.
          </p>
          {!isPublished && (
            <Button variant="secondary" onClick={() => run(remove.mutateAsync(existing.id))}>
              ביטול הקביעות
            </Button>
          )}
        </>
      ) : existing ? (
        // תבנית אחת ליום לאותו אדם, כמו הכלל בשיבוץ עצמו
        <p className="m-0 text-body-sm text-muted">
          כבר יש משמרת קבועה אחרת ביום {weekdayName(assignment.date)}. כדי לשנות, צריך לבטל
          אותה קודם מהמשמרת שבה היא נקבעה.
        </p>
      ) : (
        !isPublished && (
          <>
            <p className="m-0 text-body-sm text-muted">
              קביעת המשמרת הזו כקבועה תשבץ אותה אוטומטית בכל שבוע חדש שייפתח, באותו יום,
              באותה משמרת ובאותו סניף.
            </p>
            <Button
              variant="secondary"
              disabled={create.isPending}
              onClick={() =>
                run(
                  create.mutateAsync({
                    employee_id: assignment.employee_id,
                    branch_id: assignment.branch_id,
                    weekday,
                    shift_type_id: assignment.shift_type_id,
                    start_time: assignment.start_time,
                    end_time: assignment.end_time,
                    // מהשבוע הבא: השבוע הזה כבר משובץ
                    starts_on: nextWeekOf(assignment.date),
                  }),
                )
              }
            >
              <Repeat size={18} strokeWidth={1.75} aria-hidden="true" />
              קביעה כמשמרת קבועה
            </Button>
          </>
        )
      )}
    </section>
  );
}

/** יום ראשון של השבוע שאחרי התאריך הנתון. */
function nextWeekOf(date: string): string {
  const day = new Date(`${date}T12:00:00`);
  day.setDate(day.getDate() - day.getDay() + 7);
  return toDateKey(day);
}

/**
 * משימות שצמודות למשמרת הזו.
 *
 * כאן ולא במסך המשימות, כי זה הרגע שבו יודעים מי עובד ומתי.
 * תאריך היעד נקבע בשרת לסוף המשמרת בפועל, כדי שהמשימה לא תצוף
 * אצל העובד ביום חופש.
 */
function ShiftTasks({
  assignment,
  jobTitleId,
  isPublished,
}: {
  assignment: WeekAssignment;
  jobTitleId: string | null;
  isPublished: boolean;
}) {
  const tasks = useShiftTasks(assignment.id);
  const templates = useTaskTemplates(assignment.branch_id, jobTitleId);
  const { add, addFromTemplate, remove } = useShiftTaskMutations(assignment.id);
  const [title, setTitle] = useState('');
  const [requiresPhoto, setRequiresPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // תבנית שכבר נוספה למשמרת הזו לא מוצעת שוב
  const taken = new Set((tasks.data ?? []).map((task) => task.title));
  const offered = (templates.data ?? []).filter((one) => !taken.has(one.title));

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (title.trim() === '') return;
    setError(null);
    void add
      .mutateAsync({ title: title.trim(), requires_photo: requiresPhoto })
      .then(() => {
        setTitle('');
        setRequiresPhoto(false);
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }

  return (
    <section className="grid gap-2 border-t border-line pt-4">
      <h3 className="m-0 text-body font-medium text-ink">
        משימות למשמרת{' '}
        {tasks.data && tasks.data.length > 0 && (
          <span className="text-muted">({tasks.data.length})</span>
        )}
      </h3>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {tasks.data && tasks.data.length > 0 && (
        <ul className="m-0 grid list-none gap-1 p-0">
          {tasks.data.map((task) => (
            <li
              key={task.id}
              className="flex items-start gap-2 rounded-sm border border-line bg-cream p-2"
            >
              <span className="grid min-w-0 grow gap-0.5">
                <span
                  className={cn(
                    'text-body-sm',
                    task.status?.is_done_state ? 'text-muted line-through' : 'text-ink',
                  )}
                >
                  {task.title}
                </span>
                <span className="flex items-center gap-2 text-label text-muted">
                  {task.status?.name ?? ''}
                  {task.requires_photo && (
                    <span className="inline-flex items-center gap-1">
                      <Camera size={11} strokeWidth={2} aria-hidden="true" />
                      דורשת צילום
                    </span>
                  )}
                </span>
              </span>
              {!isPublished && (
                <button
                  type="button"
                  aria-label={`מחיקת המשימה ${task.title}`}
                  onClick={() => void remove.mutateAsync(task.id)}
                  className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-raised hover:text-ink"
                >
                  <X size={14} strokeWidth={2} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/*
        התבניות קודם, והכתיבה החופשית אחריהן: מה שחוזר צריך
        להיות לחיצה, ומה שחד פעמי עדיין אפשרי. אם כל משימה היתה
        מחייבת תבנית, איש לא היה משתמש בזה.
      */}
      {!isPublished && offered.length > 0 && (
        <div className="grid gap-1">
          <span className="text-label text-muted">בחירה מתבנית</span>
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
            {offered.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  disabled={addFromTemplate.isPending}
                  onClick={() => {
                    setError(null);
                    void addFromTemplate
                      .mutateAsync({ template_id: template.id, requires_photo: null })
                      .catch((caught: unknown) => setError(errorMessage(caught)));
                  }}
                  className="inline-flex min-h-9 cursor-pointer items-center gap-1 rounded-pill border border-muted bg-transparent px-2.5 text-label text-ink transition-colors hover:border-ink hover:bg-cream"
                >
                  <Plus size={12} strokeWidth={2.5} aria-hidden="true" />
                  {template.title}
                  {template.requires_photo && (
                    <Camera size={11} strokeWidth={2} aria-label="דורשת צילום" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isPublished && (
        <form onSubmit={submit} className="grid gap-2">
          <label className="grid gap-1">
            <span className="sr-only">משימה חדשה</span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="למשל: לסדר את מדף החלונות"
              className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink placeholder:text-muted"
            />
          </label>

          <label className="flex items-center gap-2 text-body-sm text-ink">
            <input
              type="checkbox"
              checked={requiresPhoto}
              onChange={(event) => setRequiresPhoto(event.target.checked)}
              className="size-5"
            />
            דורשת צילום
          </label>

          <Button type="submit" variant="secondary" disabled={add.isPending}>
            <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
            הוספת משימה
          </Button>
          {/*
            כשאין אף תבנית, שתיקה נראית כמו תכונה חסרה. הקישור
            הוא לאן שבאמת מגדירים אותן, ולא תיאור כללי.
          */}
          <p className="m-0 text-label text-muted">
            {templates.data && templates.data.length === 0
              ? 'עוד לא הוגדרו תבניות משימה. '
              : 'משימה שחוזרת בכל משמרת כדאי להגדיר כתבנית. '}
            <Link to="/admin/ops/tasks/templates" className="text-ink">
              הגדרת תבניות
            </Link>
          </p>
        </form>
      )}

      {isPublished && (!tasks.data || tasks.data.length === 0) && (
        <p className="m-0 text-body-sm text-muted">אין משימות למשמרת הזו.</p>
      )}
    </section>
  );
}
